// Plotly date axes render in UTC and have no native timezone support.
// To show times in a chosen zone we feed Plotly a "naive" wall-clock string
// built from that zone's components, which it then plots as-is.
//
// FS-622: the zone to use is the DEVICE's, not the viewer's. A chart is always
// read as a question about the household's day — when the system ran, when the
// house heated up — so an Eastern operator reviewing a Pacific device was
// reading every event three hours late. Pass the device's IANA zone (the plot
// endpoints return it as `time_zone`); with no zone we fall back to the
// viewer's and the axis label says so.

const pad = (n: number, len = 2): string => String(n).padStart(len, '0');

// Offset lookups go through Intl, which is far too slow to call once per
// reading (a 400-day scatter plot is ~500k points). An offset only changes at
// a DST boundary, and boundaries land on an hour mark, so caching by UTC hour
// is exact and cuts the Intl calls to at most one per hour of range.
const offsetFormatters = new Map<string, Intl.DateTimeFormat>();
const offsetsByZoneHour = new Map<string, number>();
const OFFSET_CACHE_LIMIT = 200000;

const offsetFormatterFor = (timeZone: string): Intl.DateTimeFormat => {
  let formatter = offsetFormatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'longOffset' });
    offsetFormatters.set(timeZone, formatter);
  }
  return formatter;
};

/**
 * Minutes to add to a UTC instant to get wall-clock time in `timeZone`.
 * Returns null when the zone name is not one the browser knows.
 */
const zoneOffsetMinutes = (timeZone: string, d: Date): number | null => {
  const key = `${timeZone}|${Math.floor(d.getTime() / 3600000)}`;
  const cached = offsetsByZoneHour.get(key);
  if (cached !== undefined) return cached;

  let name: string;
  try {
    name = offsetFormatterFor(timeZone)
      .formatToParts(d)
      .find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    // Unknown IANA name — Intl throws a RangeError on the constructor.
    return null;
  }

  // 'longOffset' is "GMT-07:00", or plain "GMT" at zero offset.
  let minutes = 0;
  const match = /^GMT([+-])(\d{1,2})(?::(\d{2}))?$/.exec(name);
  if (match) {
    minutes = (match[1] === '-' ? -1 : 1) * (Number(match[2]) * 60 + Number(match[3] ?? 0));
  } else if (name !== 'GMT') {
    return null;
  }

  if (offsetsByZoneHour.size >= OFFSET_CACHE_LIMIT) offsetsByZoneHour.clear();
  offsetsByZoneHour.set(key, minutes);
  return minutes;
};

/**
 * Convert a UTC/ISO timestamp into a wall-clock string
 * (`YYYY-MM-DD HH:MM:SS.sss`) that Plotly plots without further tz conversion.
 *
 * `timeZone` is an IANA name (e.g. `America/Los_Angeles`). Omit it — or pass
 * one the browser does not recognise — to fall back to the viewer's own zone.
 * Returns the input unchanged if it can't be parsed.
 */
export const toPlotlyLocal = (
  timestamp: string | number | Date,
  timeZone?: string | null,
): string => {
  const d = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (Number.isNaN(d.getTime())) return String(timestamp);

  if (timeZone) {
    const offset = zoneOffsetMinutes(timeZone, d);
    if (offset !== null) {
      // Shift the instant so the UTC getters read out the device's wall clock.
      const shifted = new Date(d.getTime() + offset * 60000);
      return (
        `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(shifted.getUTCDate())} ` +
        `${pad(shifted.getUTCHours())}:${pad(shifted.getUTCMinutes())}:` +
        `${pad(shifted.getUTCSeconds())}.${pad(shifted.getUTCMilliseconds(), 3)}`
      );
    }
  }

  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`
  );
};

type ZoneNameStyle = 'short' | 'shortGeneric';

/**
 * The zone's name at `ref` in the given Intl style. Falls back to the viewer's
 * zone when `timeZone` is missing or unrecognised.
 */
const zoneNameAt = (timeZone: string | null | undefined, ref: Date, style: ZoneNameStyle): string => {
  const options: Intl.DateTimeFormatOptions = { timeZoneName: style };
  if (timeZone) {
    try {
      const parts = new Intl.DateTimeFormat(undefined, { ...options, timeZone }).formatToParts(ref);
      return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
    } catch {
      // Unknown zone — fall through to the viewer's.
    }
  }
  const parts = new Intl.DateTimeFormat(undefined, options).formatToParts(ref);
  return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
};

/**
 * Short timezone abbreviation (e.g. "EDT", "PST") for an IANA zone at `ref` —
 * matches the `timeZoneName: 'short'` convention used by formatTimestamp.ts.
 * The abbreviation depends on the date, so the caller names the date it is
 * for. Falls back to the viewer's zone when `timeZone` is missing or
 * unrecognised.
 */
export const timeZoneAbbr = (timeZone: string | null | undefined, ref: Date): string =>
  zoneNameAt(timeZone, ref, 'short');

const knownTimeZone = (timeZone: string): boolean => {
  try {
    offsetFormatterFor(timeZone);
    return true;
  } catch {
    // Intl throws a RangeError for a zone name it does not know.
    return false;
  }
};

/**
 * The zone's name for the times on a chart (FS-1226): its abbreviation when
 * they all share one ("EDT"), or its generic name ("ET") when they cross a
 * clock change, since no single abbreviation is right for all of them. With no
 * times there is no season to name, so it is the generic name too; that name
 * is the same in every season, so today's date serves to look it up.
 * `timeZone` undefined means the viewer's zone.
 */
const zoneNameForTimes = (
  timeZone: string | undefined,
  times: readonly (string | number | Date)[],
): string => {
  let first: Date | undefined;
  let firstOffset: number | null = null;
  for (const timestamp of times) {
    const d = timestamp instanceof Date ? timestamp : new Date(timestamp);
    if (Number.isNaN(d.getTime())) continue;
    const offset = timeZone ? zoneOffsetMinutes(timeZone, d) : -d.getTimezoneOffset();
    if (!first) {
      first = d;
      firstOffset = offset;
    } else if (offset !== firstOffset) {
      return zoneNameAt(timeZone, first, 'shortGeneric');
    }
  }
  return first ? timeZoneAbbr(timeZone, first) : zoneNameAt(timeZone, new Date(), 'shortGeneric');
};

/**
 * X-axis title for a chart drawn with `toPlotlyLocal`, naming the zone for the
 * `times` the chart shows, never for the day it is opened. When the device's
 * zone is unknown the chart is drawn in the viewer's zone, so it says which
 * one rather than leaving the reader to assume it is the device's.
 */
export const axisTimeLabel = (
  timeZone: string | null | undefined,
  times: readonly (string | number | Date)[],
): string => {
  if (timeZone && knownTimeZone(timeZone)) {
    return `Time (${zoneNameForTimes(timeZone, times)} — device local time)`;
  }
  return `Time (${zoneNameForTimes(undefined, times)} — your time; device timezone unknown)`;
};
